import { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import { supabase } from '../lib/supabase';

export type PlanType = 'free' | 'basic' | 'pro';

export const ROUTE_LIMITS: Record<PlanType, number> = {
  free: 3,
  basic: 10,
  pro: Infinity,
};

export interface PlanPermissions {
  canUseShift: boolean;
  canUseDeliveries: boolean;
  canUseStatistics: boolean;
  canUseRoutes: boolean;
  routesPerDay: number;
}

export const PLAN_PERMISSIONS: Record<PlanType, PlanPermissions> = {
  free: { canUseShift: false, canUseDeliveries: false, canUseStatistics: false, canUseRoutes: true, routesPerDay: 3 },
  basic: { canUseShift: false, canUseDeliveries: false, canUseStatistics: false, canUseRoutes: true, routesPerDay: 10 },
  pro: { canUseShift: true, canUseDeliveries: true, canUseStatistics: true, canUseRoutes: true, routesPerDay: Infinity },
};

const ROUTE_USAGE_KEY = 'route_usage';

interface RouteUsageData {
  date: string;
  count: number;
}

function getTodayString(): string {
  return new Date().toISOString().split('T')[0];
}

export function getRoutesUsedToday(userId: string): number {
  try {
    const raw = localStorage.getItem(`${ROUTE_USAGE_KEY}_${userId}`);
    if (!raw) return 0;
    const data: RouteUsageData = JSON.parse(raw);
    if (data.date !== getTodayString()) return 0;
    return data.count;
  } catch {
    return 0;
  }
}

export function incrementRoutesUsedToday(userId: string): number {
  const current = getRoutesUsedToday(userId);
  const next = current + 1;
  try {
    const data: RouteUsageData = { date: getTodayString(), count: next };
    localStorage.setItem(`${ROUTE_USAGE_KEY}_${userId}`, JSON.stringify(data));
  } catch {}
  return next;
}

interface SubscriptionContextType {
  /** @deprecated use `plan` instead */
  isPremium: boolean;
  subscriptionStatus: string;
  plan: PlanType;
  permissions: PlanPermissions;
  loading: boolean;
  refreshSubscription: () => Promise<void>;
  routesUsedToday: number;
  refreshRoutesUsed: () => void;
  canUseRouteNow: () => boolean;
}

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

export function SubscriptionProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();

  const [isPremium, setIsPremium] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState<string>('free');
  const [plan, setPlan] = useState<PlanType>('free');
  const [loading, setLoading] = useState(false);
  const [routesUsedToday, setRoutesUsedToday] = useState(0);

  const inFlightRef = useRef(false);

  const refreshRoutesUsed = useCallback(() => {
    if (user) setRoutesUsedToday(getRoutesUsedToday(user.id));
    else setRoutesUsedToday(0);
  }, [user]);

  const resolvePlanFromStatus = (statusRaw: any): PlanType => {
    const status = (statusRaw ?? 'free').toString().toLowerCase();
    if (status === 'pro') return 'pro';
    if (status === 'basic') return 'basic';
    return 'free';
  };

  const fetchSubscription = useCallback(async () => {
    // evita 2 fetch concorrentes
    if (inFlightRef.current) return;
    inFlightRef.current = true;

    if (!user) {
      setIsPremium(false);
      setSubscriptionStatus('free');
      setPlan('free');
      setLoading(false);
      inFlightRef.current = false;
      return;
    }

    setLoading(true);

    // fallback anti “loading infinito”: se travar por qualquer motivo, libera em 5s
    const timeout = window.setTimeout(() => {
      setLoading(false);
      inFlightRef.current = false;
    }, 5000);

    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('is_premium, subscription_status')
        .eq('id', user.id)
        .maybeSingle();

      if (error) {
        console.error('Erro ao buscar assinatura:', error);
        setIsPremium(false);
        setSubscriptionStatus('free');
        setPlan('free');
      } else {
        // mesmo se data vier null, não trava
        const status = data?.subscription_status ?? 'free';
        const resolvedPlan = resolvePlanFromStatus(status);

        setSubscriptionStatus(status);
        setPlan(resolvedPlan);
        setIsPremium(data?.is_premium === true || resolvedPlan === 'pro');
      }
    } catch (err) {
      console.error('Erro inesperado ao buscar assinatura:', err);
      setIsPremium(false);
      setSubscriptionStatus('free');
      setPlan('free');
    } finally {
      window.clearTimeout(timeout);
      setLoading(false);
      inFlightRef.current = false;
    }
  }, [user]);

  useEffect(() => {
    void fetchSubscription();
  }, [fetchSubscription]);

  useEffect(() => {
    refreshRoutesUsed();
  }, [refreshRoutesUsed]);

  const refreshSubscription = async () => {
    await fetchSubscription();
  };

  const canUseRouteNow = useCallback((): boolean => {
    if (!user) return false;
    const limit = ROUTE_LIMITS[plan];
    if (limit === Infinity) return true;
    return getRoutesUsedToday(user.id) < limit;
  }, [user, plan]);

  const permissions = PLAN_PERMISSIONS[plan];

  return (
    <SubscriptionContext.Provider
      value={{
        isPremium,
        subscriptionStatus,
        plan,
        permissions,
        loading,
        refreshSubscription,
        routesUsedToday,
        refreshRoutesUsed,
        canUseRouteNow,
      }}
    >
      {children}
    </SubscriptionContext.Provider>
  );
}

export function useSubscription() {
  const context = useContext(SubscriptionContext);
  if (context === undefined) throw new Error('useSubscription must be used within a SubscriptionProvider');
  return context;
}